import React from "react"
import Icon from "../../../components/AppIcon"
import ThemeToggle from "./ThemeToggle"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
} from "../../../components/ui/sidebar"

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ key: "overview", label: "Dashboard", icon: "LayoutDashboard" }],
  },
  {
    label: "Learning",
    items: [
      { key: "courses", label: "Courses", icon: "BookOpen" },
      { key: "learning-programs", label: "Programs", icon: "Layers" },
      { key: "learning-paths", label: "Learning Paths", icon: "Route" },
      { key: "assessments", label: "Assessments", icon: "ClipboardCheck" },
    ],
  },
    {
    label: "People",
    items: [
      { key: "learners", label: "Learners", icon: "Users" },
      { key: "people-cohorts", label: "Cohorts", icon: "UsersRound" },
      { key: "people-mentors", label: "Mentors", icon: "HeartHandshake" },
      { key: "people-segments", label: "Segments", icon: "Target" },
      { key: "people-invitations", label: "Invitations", icon: "MailPlus" },
    ],
  },
  {
    label: "Commerce",
    items: [
      { key: "commerce-products", label: "Products", icon: "Package" },
      { key: "commerce-customers", label: "Customers", icon: "UserCircle" },
      { key: "commerce-orders", label: "Orders", icon: "ShoppingCart" },
      { key: "commerce-revenue", label: "Revenue", icon: "DollarSign" },
    ],
  },
  {
    label: "Credentials",
    items: [
      { key: "certificates", label: "Credentials", icon: "Award" },
      { key: "credentials-proof", label: "Proof of Learning", icon: "FileCheck" },
      { key: "credentials-verification", label: "Verification", icon: "ShieldCheck" },
      { key: "credentials-templates", label: "Credential Templates", icon: "FileText" },
      { key: "credentials-onchain", label: "On-chain Credentials", icon: "Link" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { key: "analytics", label: "Overview", icon: "BarChart3" },
      { key: "intelligence-learners", label: "Learners", icon: "Users" },
      { key: "intelligence-courses", label: "Courses", icon: "BookOpen" },
      { key: "intelligence-programs", label: "Programs", icon: "Layers" },
      { key: "intelligence-commerce", label: "Commerce", icon: "DollarSign" },
      { key: "intelligence-outcomes", label: "Outcomes", icon: "TrendingUp" },
    ],
  },
  {
    label: "Engagement",
    items: [
      { key: "engagement-activity", label: "Activity", icon: "Activity" },
      { key: "engagement-announcements", label: "Announcements", icon: "Megaphone" },
      { key: "engagement-communications", label: "Communications", icon: "MessageSquare" },
    ],
  },
  {
    label: "Developer",
    items: [
      { key: "developer-api-keys", label: "API Keys", icon: "KeyRound" },
      { key: "developer-webhooks", label: "Webhooks", icon: "Webhook" },
      { key: "developer-api-usage", label: "API Usage", icon: "Gauge" },
      { key: "developer-docs", label: "Developer Docs", icon: "BookText" },
    ],
  },
  {
    label: "Integrations",
    items: [
      { key: "integrations-connected", label: "Connected Apps", icon: "Plug" },
      { key: "integrations-marketplace", label: "Integration Marketplace", icon: "Store" },
    ],
  },
  {
    label: "Organization",
    items: [
      { key: "team-workspace", label: "Team Members", icon: "UserPlus" },
      { key: "org-roles", label: "Roles & Permissions", icon: "ShieldCheck" },
      { key: "org-audit-log", label: "Audit Log", icon: "ScrollText" },
    ],
  },
  {
    label: "Billing",
    items: [
      { key: "billing-subscription", label: "Subscription", icon: "CreditCard" },
      { key: "billing-usage", label: "Usage", icon: "Gauge" },
      { key: "billing-invoices", label: "Invoices", icon: "Receipt" },
      { key: "billing-payment-methods", label: "Payment Methods", icon: "Wallet" },
    ],
  },
  {
    label: "Settings",
    items: [
      { key: "settings", label: "Organization", icon: "Settings" },
      { key: "settings-branding", label: "Branding", icon: "Palette" },
      { key: "settings-learning", label: "Learning", icon: "BookOpen" },
      { key: "settings-credentials", label: "Credentials", icon: "Award" },
      { key: "settings-notifications", label: "Notifications", icon: "Bell" },
      { key: "settings-security", label: "Security", icon: "Lock" },
      { key: "settings-privacy", label: "Privacy", icon: "ShieldQuestion" },
    ],
  },
]

const DashboardSidebar = ({ activeTab, onTabChange, organization }) => {
  return (
    <Sidebar collapsible="icon" variant="sidebar" className="!top-16 !h-[calc(100svh-4rem)]">
      <SidebarHeader className="flex-row items-center justify-between gap-2 border-b border-sidebar-border py-3">
        <span className="text-xs font-mono text-sidebar-foreground/70 truncate group-data-[collapsible=icon]:hidden">
          {organization?.nesolearnId || organization?.orgId || "—"}
        </span>
        <SidebarTrigger />
      </SidebarHeader>

            <SidebarContent>
        {NAV_SECTIONS?.map((section) => (
          <SidebarGroup key={section?.label}>
            <SidebarGroupLabel>{section?.label}</SidebarGroupLabel>
            <SidebarMenu>
              {section?.items?.map((item) => (
                <SidebarMenuItem key={item?.key}>
                  <SidebarMenuButton
                    isActive={activeTab === item?.key}
                    tooltip={item?.label}
                    onClick={() => onTabChange(item?.key)}
                  >
                    <Icon name={item?.icon} size={16} />
                    <span>{item?.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

            <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem className="flex flex-row items-center gap-1">
            <SidebarMenuButton onClick={() => onTabChange("settings")} tooltip="Settings" className="flex-1">
              {organization?.logoUrl ? (
                <img src={organization?.logoUrl} alt={organization?.name} className="size-4 rounded object-cover shrink-0" />
              ) : (
                <Icon name="Building2" size={16} />
              )}
              <span className="truncate">{organization?.name || "Settings"}</span>
            </SidebarMenuButton>
            <div className="group-data-[collapsible=icon]:hidden">
              <ThemeToggle />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}

export default DashboardSidebar